import { useEffect, useState } from "react";
import chatService from "../../services/conversations/conversationsService";
import { ConversationUpdatedEventArgs, Message } from "@twilio/conversations";
import {
  Agent,
  AgentEvent,
  ConversationServiceReadyEvent,
  Customer,
} from "../../services/conversations/definitions/types";
import {
  AgentActions,
  ConversationEvents,
} from "../../services/conversations/definitions/constants";
import { UseLiveChatProps, LiveChatAttributes } from "./definitions";

const useLiveChat = ({ liveChatId, token, identity }: UseLiveChatProps) => {
  const [agentEvents, setAgentEvents] = useState<AgentEvent[]>([]);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [liveChatAttributes, setLiveChatAttributes] =
    useState<LiveChatAttributes>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [dateCreated, setDateCreated] = useState<Date | null>(null);

  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [isLiveChatReady, setLiveChatReady] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [sdkError, setSdkError] = useState("");

  useEffect(() => {
    if (!chatService.isServiceReady) {
      chatService.addEventListener<string>(
        ConversationEvents.Error,
        setSdkError
      );
      chatService.addEventListener<boolean>(
        ConversationEvents.Typing,
        setIsAgentTyping
      );
      chatService.addEventListener<Message[]>(
        ConversationEvents.Messages,
        setMessages
      );
      chatService.addEventListener<ConversationServiceReadyEvent>(
        ConversationEvents.Ready,
        handleChatServiceReady
      );
      chatService.addEventListener<AgentEvent>(
        ConversationEvents.Agent,
        handleAgentEvent
      );
      chatService.addEventListener<ConversationUpdatedEventArgs>(
        ConversationEvents.Updated,
        handleConversationUpdated
      );
      chatService.initService({
        token,
        liveChatId,
        identity,
      });

      return () => {
        removeListeners();
      };
    }
  }, []);

  const removeListeners = (): void => {
    chatService.removeEventListener<string>(
      ConversationEvents.Error,
      setSdkError
    );
    chatService.removeEventListener<boolean>(
      ConversationEvents.Typing,
      setIsAgentTyping
    );
    chatService.removeEventListener<Message[]>(
      ConversationEvents.Messages,
      setMessages
    );
    chatService.removeEventListener<ConversationServiceReadyEvent>(
      ConversationEvents.Ready,
      handleChatServiceReady
    );
    chatService.removeEventListener<ConversationUpdatedEventArgs>(
      ConversationEvents.Updated,
      handleConversationUpdated
    );
    chatService.removeEventListener<AgentEvent>(
      ConversationEvents.Agent,
      handleAgentEvent
    );
    chatService.isServiceReady = false;
  };

  // Callback for when chat attribute is updated
  const handleUpdatedLiveChatAttributes = (
    event: ConversationUpdatedEventArgs
  ): void => {
    setLiveChatAttributes((prevState) => {
      const attributes = event.conversation?.attributes as LiveChatAttributes;
      if (!attributes) {
        return;
      }

      if (!prevState) {
        return attributes;
      }

      return {
        ...prevState,
        ...attributes,
      };
    });
  };

  // Callback for when the chat data is updated
  const handleConversationUpdated = (
    event: ConversationUpdatedEventArgs
  ): void => {
    if (event.updateReasons.includes("attributes")) {
      handleUpdatedLiveChatAttributes(event);
    }

    // Add other handlers here as needed...
  };

  // Callback for when the chat service is ready
  const handleChatServiceReady = (
    event: ConversationServiceReadyEvent
  ): void => {
    setLiveChatReady(event.isServiceReady);
    setAgent(event.agent);
    setCustomer(event.customer);
    setDateCreated(event.dateCreated);
    setLiveChatAttributes(event.attributes as LiveChatAttributes);
  };

  // Callback to receive the name of the agent joined and pass to the respective component
  const handleAgentEvent = (event: AgentEvent): void => {
    setAgentEvents((prevAgentEvents) => [...prevAgentEvents, event]);

    if (event.action === AgentActions.Join) {
      setAgent(event.agent);
    } else {
      // Notification on Agent Left
      setAgent(null);
    }
  };

  const clearError = (): void => {
    setSdkError("");
  };

  return {
    agentEvents,
    agent,
    customer,
    dateCreated,
    liveChatAttributes,
    messages,
    sdkError,
    clearError,
    isAgentTyping,
    isLiveChatReady,
  };
};

export default useLiveChat;
